import { useCallback, useEffect, useRef, useState } from "react";

import { playerReleaseService } from "cortex/ui/src/service/playerRelease";

export type PlayerVersion = {
  is_latest?: boolean;
  type: string;
  version: string;
  update_date: string;
};

export type ReleaseHistory = {
  binary_type: string;
  commits: string[];
  id: number;
  notes: string;
  release_date: string;
  version: string;
};

type State = {
  data: PlayerVersion[];
  loading: boolean;
  error: string | null;
};

export function usePlayerVersions(): State {
  const [state, setState] = useState<State>({
    data: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let alive = true;

    async function run() {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const res = await playerReleaseService.loadAllVersions();
        const raw = Array.isArray((res as any)?.data?.data)
          ? (res as any).data.data
          : res;

        const data: PlayerVersion[] = (raw ?? []).map((pv: any) => ({
          type: pv.type,
          version: pv.version,
          update_date: pv.update_date,
          is_latest: pv.is_latest,
        }));

        if (alive) setState({ data, loading: false, error: null });
      } catch (e: any) {
        if (alive)
          setState({
            data: [],
            loading: false,
            error: e?.message ?? "Failed to fetch player versions",
          });
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

export function useReleaseHistory() {
  const [history, setHistory] = useState<ReleaseHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    async function fetchHistory() {
      setLoading(true);
      setError(null);
      try {
        const res = await playerReleaseService.getReleaseHistory();
        const raw = Array.isArray((res as any)?.data?.data)
          ? (res as any).data.data
          : res;

        const data: ReleaseHistory[] = (raw ?? []).map((pv: any) => ({
          binary_type: pv.binary_type,
          commits: pv.commits,
          id: pv.id,
          notes: pv.notes,
          release_date: pv.release_date,
          version: pv.version,
        }));

        if (alive) {
          setHistory(data);
          setLoading(false);
        }
      } catch (e: any) {
        if (alive) {
          setError(e?.message ?? "Failed to fetch release history");
          setLoading(false);
        }
      }
    }

    fetchHistory();
    return () => {
      alive = false;
    };
  }, []);

  return { data: history, loading, error };
}

export function useLatestVersions() {
  const [history, setHistory] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const aliveRef = useRef(true);

  const fetchLatestVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await playerReleaseService.getLatestVersions();
      const data = ((res as any)?.data?.data as Record<string, string>) || {};

      if (!aliveRef.current) return;

      setHistory(data);
      setLoading(false);
    } catch (e: any) {
      if (!aliveRef.current) return;

      setError(e?.message ?? "Failed to fetch latest versions");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    fetchLatestVersions();

    return () => {
      aliveRef.current = false;
    };
  }, [fetchLatestVersions]);

  return { data: history, loading, error, refetch: fetchLatestVersions };
}

export type EditLatestVersionPayload = {
  type: string;
  version: string;
};

type EditLatestVersionState = {
  loading: boolean;
  error: string | null;
};

export function useEditLatestVersion() {
  const [state, setState] = useState<EditLatestVersionState>({
    loading: false,
    error: null,
  });

  const editLatestVersion = useCallback(
    async ({ type, version }: EditLatestVersionPayload) => {
      setState({ loading: true, error: null });

      try {
        await playerReleaseService.editLatestVersions([
          {
            type,
            version,
          },
        ]);

        setState({ loading: false, error: null });
      } catch (e: any) {
        setState({
          loading: false,
          error: e?.message ?? "Failed to update latest version",
        });
        // rethrow if caller wants to handle details
        throw e;
      }
    },
    [],
  );

  return {
    editLatestVersion,
    loading: state.loading,
    error: state.error,
  };
}

export type DeviceAccountStat = {
  account_id: number;
  account_name: string;
  count: number;
  device_ids: string[];
};

export type DeviceVersionStat = {
  version: string;
  count: number;
  pct: number;
  accounts: DeviceAccountStat[];
};

export type DeviceOsGroup = {
  os: string;
  count: number;
  pct: number;
  versions: DeviceVersionStat[];
};

export type DeviceKind = "nwjs-player" | "kmp-player" | "agent" | "thirdparty";

export type DeviceKindGroup = {
  kind: DeviceKind;
  count: number;
  pct: number;
  by_os: DeviceOsGroup[];
};

export type DeviceVersionStats = {
  total: number;
  lookback_days: number;
  by_kind: DeviceKindGroup[];
};

type DeviceVersionStatsState = {
  data: DeviceVersionStats | null;
  loading: boolean;
  computing: boolean;
  error: string | null;
};

// Normalize the response payload.  Three known shapes are accepted:
//
//  1. New (current): { by_kind: [{kind, by_os: [...]}, ...] }
//  2. Pre-kind:      { by_os: [...] }                       — wrapped as one
//                                                            synthetic "player"
//                                                            kind so cached
//                                                            entries from
//                                                            before the kind
//                                                            change still
//                                                            render.
//  3. Pre-accounts:  { by_os: [...] } with device_ids on versions instead of
//                    accounts                               — accounts are
//                                                            synthesized as a
//                                                            single "All
//                                                            accounts" bucket.
//
// The hook is the single funnel for all responses, so normalizing here means
// the rest of the UI only ever sees shape (1).
function normalizeDeviceVersionStats(payload: any): DeviceVersionStats | null {
  if (!payload) return null;

  const normalizeAccounts = (v: any): DeviceAccountStat[] => {
    if (Array.isArray(v.accounts)) {
      return v.accounts.map((a: any) => ({
        account_id: a.account_id,
        account_name: a.account_name,
        count: a.count,
        device_ids: a.device_ids ?? [],
      }));
    }
    // Pre-accounts shape: device_ids on the version itself.
    return [
      {
        account_id: 0,
        account_name: "All accounts",
        count: v.count,
        device_ids: v.device_ids ?? [],
      },
    ];
  };

  const normalizeVersions = (vs: any[]): DeviceVersionStat[] =>
    (vs ?? []).map((v: any) => ({
      version: v.version,
      count: v.count,
      pct: v.pct,
      accounts: normalizeAccounts(v),
    }));

  const normalizeOsGroups = (gs: any[]): DeviceOsGroup[] =>
    (gs ?? []).map((g: any) => ({
      os: g.os,
      count: g.count,
      pct: g.pct,
      versions: normalizeVersions(g.versions),
    }));

  if (Array.isArray(payload.by_kind)) {
    return {
      total: payload.total,
      lookback_days: payload.lookback_days,
      by_kind: payload.by_kind.map((k: any) => ({
        kind: k.kind,
        count: k.count,
        pct: k.pct,
        by_os: normalizeOsGroups(k.by_os),
      })),
    };
  }

  // Pre-kind shape — fold the flat by_os list under a synthetic "nwjs-player"
  // kind so the UI has uniform structure to render.  That shape predates KMP,
  // so every device in it is nwjs.
  const byOs = normalizeOsGroups(payload.by_os ?? []);
  const total = payload.total ?? byOs.reduce((s, g) => s + g.count, 0);
  return {
    total,
    lookback_days: payload.lookback_days,
    by_kind:
      byOs.length === 0
        ? []
        : [
            {
              kind: "nwjs-player",
              count: total,
              pct: 100,
              by_os: byOs,
            },
          ],
  };
}

export function useDeviceVersionStats(): DeviceVersionStatsState {
  const [state, setState] = useState<DeviceVersionStatsState>({
    data: null,
    loading: true,
    computing: false,
    error: null,
  });
  const aliveRef = useRef(true);
  const retryCountRef = useRef(0);

  const fetchStats = useCallback(async () => {
    try {
      const res = await playerReleaseService.getDeviceVersionStats();

      if (!aliveRef.current) return;

      if ((res as any).status === 202) {
        setState((s) => ({ ...s, loading: false, computing: true }));
        return;
      }

      const payload = (res as any)?.data?.data as any;
      const normalized = normalizeDeviceVersionStats(payload);
      retryCountRef.current = 0;
      setState({
        data: normalized,
        loading: false,
        computing: false,
        error: null,
      });
    } catch (e: any) {
      if (!aliveRef.current) return;
      setState({
        data: null,
        loading: false,
        computing: false,
        error: e?.message ?? "Failed to fetch device version stats",
      });
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchStats();
    return () => {
      aliveRef.current = false;
    };
  }, [fetchStats]);

  // Exponential back-off retry on 202: 3s → 4.5s → 6.75s → 10s ceiling
  useEffect(() => {
    if (!state.computing) {
      retryCountRef.current = 0;
      return;
    }
    const delay = Math.min(3000 * Math.pow(1.5, retryCountRef.current), 10000);
    retryCountRef.current += 1;
    const t = setTimeout(fetchStats, delay);
    return () => clearTimeout(t);
  }, [state.computing, fetchStats]);

  return state;
}
